import {Injectable} from '@angular/core';
import {ShellClientService} from './shell-communication/shell-client.service';
import {
    GetImpliedVolatility,
    GetImpliedVolatilityReply
} from './shell-communication/shell-operations-protocol';

/**
 * Coalesces IV requests across all consumers (Hedge Matrix dayOfWeek cells, OPG header,
 * APG volatility header, etc.).
 *
 * Without this, each consumer fires its own HTTP per quote-tick or audit-pulse.
 * Multiple panels (HM + OPG + APG) targeting the same (underlying, expiration) on the
 * same quote tick produced N concurrent HTTPs — backend coalesced compute via its
 * in-flight tracking, but the network round-trips and reply payloads still multiplied.
 *
 * Two-stage coalescing:
 *  1. In-flight: peers calling within the same HTTP window share one Promise.
 *  2. TTL cache: peers calling within {@link _ttlMs} after a reply share the cached value.
 *
 * Rejections are cached too with the same TTL: a consumer that retries on error (e.g. the
 * iv-day-of-week-cell-renderer's bounded backoff) doesn't drag peer consumers along —
 * peers see the cached stub and decide for themselves whether to wait or render N/A.
 */
@Injectable({providedIn: 'root'})
export class IvCacheService {

    private static readonly MONTH_MAP: Record<string, string> = {
        Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
        Jul: '07', Aug: '08', Sep: '09', Oct: '10', Nov: '11', Dec: '12'
    };

    private readonly _ttlMs = 2000;
    private readonly _cache = new Map<string, { reply: GetImpliedVolatilityReply, ts: number }>();
    private readonly _inflight = new Map<string, Promise<GetImpliedVolatilityReply>>();

    constructor(private readonly _shellClient: ShellClientService) {
    }

    /**
     * Returns IV reply for ({@link underlying}, {@link expiration}). Concurrent peers
     * within the in-flight window share one HTTP. Subsequent peers within the TTL share
     * the cached reply. Rejections also cache (as null-volatility stub) but re-throw to
     * the caller so existing try/catch consumer logic keeps working.
     *
     * The expiration string is normalized to canonical 'yyyy-MM-dd' before keying so
     * that callers passing 'dd-MMM-yy' (e.g. APG) and callers passing 'yyyy-MM-dd'
     * (e.g. HM, OPG) for the same date hit the same cache slot.
     */
    getIv(underlying: string, expiration: string): Promise<GetImpliedVolatilityReply> {
        const canonical = IvCacheService.normalizeExpiration(expiration);
        const key = underlying + '|' + canonical;

        const cached = this._cache.get(key);
        if (cached && (Date.now() - cached.ts) < this._ttlMs) {
            return Promise.resolve(cached.reply);
        }

        const inflight = this._inflight.get(key);
        if (inflight) {
            return inflight;
        }

        const promise = this._shellClient
            .processQuery<GetImpliedVolatilityReply>(new GetImpliedVolatility(underlying, canonical))
            .then(reply => {
                this._cache.set(key, {reply, ts: Date.now()});
                return reply;
            })
            .catch(err => {
                // Cache the rejection as a null-volatility stub so peer consumers in the
                // same TTL window don't all fire fresh HTTPs after the first one fails.
                // The caller still sees the original error (we re-throw) so retry/backoff
                // logic in the consumer keeps its existing semantics.
                const stub = {
                    underlying,
                    expiration: canonical,
                    volatility: null,
                    strike: null
                } as GetImpliedVolatilityReply;
                this._cache.set(key, {reply: stub, ts: Date.now()});
                throw err;
            })
            .finally(() => {
                this._inflight.delete(key);
            });

        this._inflight.set(key, promise);
        return promise;
    }

    /**
     * Normalizes expiration date to canonical 'yyyy-MM-dd'. Accepts 'yyyy-MM-dd'
     * (returned as-is) or 'dd-MMM-yy' (e.g. '04-May-26'). Unrecognized inputs are
     * returned unchanged — they'll still be valid cache keys, just per-format.
     */
    private static normalizeExpiration(expiration: string): string {
        if (/^\d{4}-\d{2}-\d{2}$/.test(expiration)) {
            return expiration;
        }
        const m = expiration.match(/^(\d{2})-([A-Za-z]{3})-(\d{2,4})$/);
        if (!m) {
            return expiration;
        }
        const dd = m[1];
        const mon = m[2];
        const yy = m[3];
        const mm = IvCacheService.MONTH_MAP[mon.charAt(0).toUpperCase() + mon.slice(1).toLowerCase()];
        if (!mm) {
            return expiration;
        }
        const year = yy.length === 2 ? '20' + yy : yy;
        return year + '-' + mm + '-' + dd;
    }
}
